/**
 * AYU Dashboard — Personalisierter Startbereich
 * Design: Ayurveda Garden / Hell & Natürlich
 * Dynamisch: Kundendaten aus Auth-Context + echte Stats
 * Inspiriert von MENTO Dashboard mit AYU-Branding
 */
import { useState, useEffect, useMemo } from "react";
import { Link } from "wouter";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useDemo } from "@/contexts/DemoContext";
import { trpc } from "@/lib/trpc";
import { useI18n } from "@/contexts/I18nContext";
import {
  BookOpen,
  MessageCircle,
  Compass,
  Calendar,
  Sparkles,
  ArrowRight,
  Sun,
  Moon,
  Cloud,
  Flame,
  Loader2,
  TrendingUp,
  Send as SendIcon,
  CheckCircle2,
  MessageSquare,
  Heart,
  Wind,
  Droplets,
  Crown,
  Sparkle,
  ArrowUpRight,
  Dna,
  Users,
  ExternalLink,
  Headphones,
  Play,
  BarChart3,
  Clock,
  Check,
  Award,
} from "lucide-react";
import { toast } from "sonner";
import { getDailyQuote } from "@/config/dailyInspiration";
import GamificationWidget from "@/components/GamificationWidget";
import PromoCodeInput from "@/components/PromoCodeInput";
import { PHENOTYPES, type PhenotypeId } from "@shared/phenotypeScoring";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { ClipboardCheck, Lock } from "lucide-react";

/* ─── Mood Config ─── */
const MOOD_CONFIG: Record<
  string,
  { emoji: string; label: string; value: number; color: string }
> = {
  great: { emoji: "\u{1F60A}", label: "Super", value: 5, color: "#6b7c3e" },
  good: { emoji: "\u{1F642}", label: "Gut", value: 4, color: "#8FA89A" },
  neutral: { emoji: "\u{1F610}", label: "Okay", value: 3, color: "#6b7f4e" },
  low: { emoji: "\u{1F614}", label: "Meh", value: 2, color: "#E8A87C" },
  bad: { emoji: "\u{1F622}", label: "Schlecht", value: 1, color: "#E8856C" },
  "sehr gut": {
    emoji: "\u{1F60A}",
    label: "Super",
    value: 5,
    color: "#6b7c3e",
  },
  gut: { emoji: "\u{1F642}", label: "Gut", value: 4, color: "#8FA89A" },
  okay: { emoji: "\u{1F610}", label: "Okay", value: 3, color: "#6b7f4e" },
  schlecht: {
    emoji: "\u{1F614}",
    label: "Schlecht",
    value: 2,
    color: "#E8A87C",
  },
  "sehr schlecht": {
    emoji: "\u{1F622}",
    label: "Sehr schlecht",
    value: 1,
    color: "#E8856C",
  },
  energiegeladen: {
    emoji: "⚡",
    label: "Energiegeladen",
    value: 5,
    color: "#6b7f4e",
  },
  müde: { emoji: "\u{1F634}", label: "Müde", value: 2, color: "#8b7a5e" },
  gestresst: {
    emoji: "\u{1F624}",
    label: "Gestresst",
    value: 2,
    color: "#E8856C",
  },
  entspannt: {
    emoji: "\u{1F9D8}",
    label: "Entspannt",
    value: 4,
    color: "#6b7c3e",
  },
  glücklich: {
    emoji: "\u{1F604}",
    label: "Glücklich",
    value: 5,
    color: "#6b7c3e",
  },
};

/* ─── Helpers ─── */
function getGreetingByTime(t: (key: string) => string): string {
  const hour = new Date().getHours();
  if (hour < 6) return t("dashboard.greetingNight");
  if (hour < 12) return t("dashboard.greetingMorning");
  if (hour < 17) return t("dashboard.greetingDay");
  if (hour < 21) return t("dashboard.greetingEvening");
  return t("dashboard.greetingNight");
}

function getGreetingIcon() {
  const hour = new Date().getHours();
  if (hour < 6 || hour >= 21) return Moon;
  if (hour < 12) return Sun;
  if (hour < 17) return Sun;
  return Cloud;
}

/* ─── Upgrade Button ─── */
function UpgradeButton() {
  const { isDemo } = useDemo();
  const { t } = useI18n();
  if (isDemo) return null;
  const [isLoading, setIsLoading] = useState(false);
  const upgradeMutation = trpc.stripe.createUpgradeCheckout.useMutation({
    onSuccess: data => {
      if (data.url) {
        toast.info(t("dashboard.upgradeRedirecting"));
        window.location.href = data.url;
      }
    },
    onError: err => {
      toast.error(err.message || t("dashboard.upgradeError"));
      setIsLoading(false);
    },
  });

  const handleUpgrade = () => {
    setIsLoading(true);
    upgradeMutation.mutate({ origin: window.location.origin });
  };

  return (
    <button
      onClick={handleUpgrade}
      disabled={isLoading || upgradeMutation.isPending}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#d3ab5f] text-white text-xs font-medium hover:shadow-md hover:shadow-[#8b6914]/20 transition-all duration-200 disabled:opacity-50"
    >
      {isLoading || upgradeMutation.isPending ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <Dna className="w-3.5 h-3.5" />
      )}
      <span>{t("dashboard.upgradeBtn")}</span>
      <ArrowUpRight className="w-3 h-3" />
    </button>
  );
}

/* ─── Telegram Dashboard Card ─── */
function TelegramDashboardCard({ clientId }: { clientId: string }) {
  const { t } = useI18n();
  const BOT_URL = `https://t.me/Ayu_health_bot?start=${clientId}`;
  const [code, setCode] = useState("");
  const [justLinked, setJustLinked] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [showInput, setShowInput] = useState(false);
  const [communityJoined, setCommunityJoined] = useState(() => {
    try {
      return (
        localStorage.getItem(`ayu_community_joined_${clientId}`) === "true"
      );
    } catch {
      return false;
    }
  });

  const { data: linkData, isLoading } = trpc.telegram.getLink.useQuery({
    clientId,
  });
  const utils = trpc.useUtils();

  const linkMutation = trpc.telegram.link.useMutation({
    onSuccess: data => {
      if (data.success) {
        setJustLinked(true);
        setCode("");
        setLinkError(null);
        setShowInput(false);
        utils.telegram.getLink.invalidate({ clientId });
      } else {
        setLinkError(data.error || t("dashboard.telegramLinkError"));
      }
    },
    onError: () => setLinkError(t("dashboard.telegramConnectionError")),
  });

  const handleCommunityToggle = () => {
    const newVal = !communityJoined;
    setCommunityJoined(newVal);
    try {
      localStorage.setItem(`ayu_community_joined_${clientId}`, String(newVal));
    } catch {
      /* ignore */
    }
  };

  if (isLoading) return null;

  const isLinked = !!linkData?.telegramChatId;

  // Wenn beides erledigt (Coach verbunden + Community beigetreten), Karte komplett ausblenden
  if (isLinked && communityJoined) return null;

  return (
    <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5 space-y-4">
      {/* AYU Coach Telegram — nur anzeigen wenn NICHT verbunden */}
      {!isLinked && (
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#0088cc]/10 flex items-center justify-center shrink-0">
                <MessageSquare className="w-4 h-4 text-[#0088cc]" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-[#2d3a1e]">Annam — Telegram</h3>
                <p className="text-xs text-[#8a9680]">{t("dashboard.telegramNotConnected")}</p>
              </div>
            </div>
            <button
              onClick={() => {
                if (!showInput) window.open(BOT_URL, "_blank", "noopener,noreferrer");
                setShowInput(!showInput);
              }}
              className="flex items-center gap-1.5 text-xs font-medium text-[#0088cc] hover:text-[#0077b5] transition-colors px-2.5 py-1.5 rounded-lg hover:bg-[#0088cc]/8 shrink-0"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              {showInput ? t("common.cancel") : t("dashboard.telegramConnect")}
            </button>
          </div>

          {showInput && (
            <div className="space-y-3 mt-3 pt-3 border-t border-[#d4dcc4]">
              <div className="bg-[#f8f8f8] rounded-xl p-3">
                <ol className="text-sm text-[#5a6b4a] space-y-1.5 list-decimal list-inside">
                  <li>
                    {t("dashboard.telegramStep1")}{" "}
                    <span className="font-medium text-[#0088cc]">@Ayu_health_bot</span>
                  </li>
                  <li>
                    {t("dashboard.telegramStep2")}{" "}
                    <span className="font-mono bg-[#f0f3eb] px-1 py-0.5 rounded text-[#2d3a1e]">/start</span>
                  </li>
                  <li>{t("dashboard.telegramStep3")}</li>
                </ol>
              </div>

              <div className="flex gap-2">
                <input
                  type="text"
                  value={code}
                  onChange={e => { setCode(e.target.value.toUpperCase()); setLinkError(null); }}
                  placeholder="z.B. A3X7K2"
                  maxLength={6}
                  className="flex-1 bg-[#f0f3eb] rounded-xl px-4 py-2.5 text-sm text-[#2d3a1e] placeholder:text-[#8a9680] outline-none focus:ring-2 focus:ring-[#0088cc]/20 transition-all font-mono tracking-widest text-center border border-[#d4dcc4]"
                />
                <button
                  onClick={() => linkMutation.mutate({ clientId, code: code.trim() })}
                  disabled={code.trim().length < 4 || linkMutation.isPending}
                  className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                    code.trim().length >= 4 ? "bg-[#0088cc] text-white hover:bg-[#0077b5] shadow-sm" : "bg-[#f0f3eb] text-[#8a9680]"
                  }`}
                >
                  {linkMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <SendIcon className="w-4 h-4" />}
                </button>
              </div>

              {justLinked && (
                <div className="flex items-center gap-2 text-[#6b7c3e] bg-[#6b7c3e]/10 rounded-xl p-3">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-sm">{t("dashboard.telegramLinked")}</span>
                </div>
              )}
              {linkError && <p className="text-sm text-red-400">{linkError}</p>}
            </div>
          )}
        </div>
      )}

      {/* AYU Community Telegram — nur anzeigen wenn noch nicht als beigetreten markiert */}
      {!communityJoined && (
        <div className={!isLinked ? "pt-4 border-t border-[#d4dcc4]" : ""}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#6b7f4e]/10 flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 text-[#6b7f4e]" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-[#2d3a1e]">AYU Community — Telegram</h3>
                <p className="text-xs text-[#8a9680]">{t("dashboard.communityExchange")}</p>
              </div>
            </div>
            <a
              href={`https://t.me/Ayu_community_bot?start=ref_${clientId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs font-medium text-[#6b7f4e] hover:text-[#5a6b3e] transition-colors px-2.5 py-1.5 rounded-lg hover:bg-[#6b7f4e]/8 shrink-0"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
              </svg>
              Join
            </a>
          </div>

          <button
            onClick={handleCommunityToggle}
            className="w-full mt-3 flex items-center justify-center gap-2 text-xs text-[#8a9680] hover:text-[#6b7c3e] transition-colors py-1.5"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            {t("dashboard.communityConfirmJoined")}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Main Dashboard ─── */
/** Check-In Erinnerungsbanner */
function CheckInBanner() {
  const { t } = useI18n();
  const checkInStatus = trpc.clientAuth.checkInStatus.useQuery();
  const data = checkInStatus.data;

  // Nicht anzeigen wenn noch kein Onboarding oder Daten laden
  if (!data || (!data.isDue && !data.isLocked)) return null;
  // Nicht anzeigen wenn gesperrt und kein Onboarding abgeschlossen
  if (data.isLocked && data.daysUntilAvailable === null) return null;

  const days = data.daysSinceLastCheckIn;
  const isFirstCheckIn = !data.hasFunctionalState;

  // Gesperrt: Grau hinterlegt mit Countdown
  if (data.isLocked && data.daysUntilAvailable) {
    return (
      <div className="rounded-2xl bg-[#f0ede5]/60 border border-[#d4dcc4]/50 p-4 opacity-70">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-[#d4dcc4]/30 text-[#8a9680]">
            <Lock className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-[#8a9680]">
              {isFirstCheckIn ? t("dashboard.checkinErster") : t("dashboard.naechsterCheckin")}
            </h3>
            <p className="text-xs text-[#b5a890]">
              <Clock className="w-3 h-3 inline mr-1" />
              {t("dashboard.verfuegbarIn", { days: String(data.daysUntilAvailable) })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Fällig: Aktiv klickbar
  if (data.isDue) {
    return (
      <Link href="/app/check-in">
        <div className="rounded-2xl bg-gradient-to-r from-[#D4A853]/15 to-[#6B7B3A]/10 border border-[#D4A853]/30 p-4 cursor-pointer hover:shadow-md transition-all group">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-[#D4A853]/15 text-[#D4A853] group-hover:bg-[#D4A853]/25 transition-colors">
              <ClipboardCheck className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-[#2d3a1e]">
                {isFirstCheckIn
                  ? t("dashboard.checkinErster")
                  : t("dashboard.checkinFaellig")}
              </h3>
              <p className="text-xs text-[#8a9680]">
                {isFirstCheckIn
                  ? t("dashboard.checkinSubtitle")
                  : t("dashboard.checkinDaysAgo", { days: String(days) })}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#D4A853] group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>
    );
  }

  return null;
}

/* ─── Chapter titles for Weiterhören widget ─── */
const AUDIOBOOK_CHAPTERS: Record<string, string> = {
  vorwort: "Vorwort",
  "kapitel-01": "Kapitel 1 – Einleitung",
  "kapitel-02": "Kapitel 2 – Ayurveda in Indien",
  "kapitel-03": "Kapitel 3 – Heiltradition und Prävention",
  "kapitel-04": "Kapitel 4 – Grenzen der Übertragbarkeit",
  "kapitel-05": "Kapitel 5 – Genetische Unterschiede",
  "kapitel-06": "Kapitel 6 – Klima & Geografie",
  "kapitel-07": "Kapitel 7 – Der AYU-Ansatz",
  "kapitel-08": "Kapitel 8 – Die drei AYU-Fragebögen",
  "kapitel-09": "Kapitel 9 – Praktische Anwendung",
  "kapitel-10": "Kapitel 10 – Empfehlungen",
  "kapitel-11": "Kapitel 11 – Langfristige Prävention",
  nachwort: "Nachwort",
};

interface ProgressEntry {
  chapterId: string;
  progressPercent: number;
  currentTimeSec: number;
  durationSec: number;
  updatedAt: Date | string;
}

function ContinueListeningWidget({
  progressData,
}: {
  progressData?: ProgressEntry[];
}) {
  const { t } = useI18n();
  if (!progressData || progressData.length === 0) return null;

  // Find the most recently updated chapter that is not 100% complete
  const inProgress = progressData
    .filter(p => p.progressPercent < 100 && p.progressPercent > 0)
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );

  if (inProgress.length === 0) return null;

  const latest = inProgress[0];
  const chapterTitle = AUDIOBOOK_CHAPTERS[latest.chapterId] || latest.chapterId;
  const progressPct = Math.round(latest.progressPercent);

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <Link href="/hoerbuch">
      <div className="group rounded-2xl border border-[#C9A96E]/30 bg-gradient-to-r from-[#C9A96E]/10 to-[#6b7f4e]/5 p-4 cursor-pointer hover:shadow-md hover:border-[#C9A96E]/50 transition-all duration-300">
        <div className="flex items-center gap-4">
          {/* Icon */}
          <div className="w-12 h-12 rounded-xl bg-[#C9A96E]/20 flex items-center justify-center shrink-0">
            <Headphones className="w-6 h-6 text-[#C9A96E]" />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-xs font-medium text-[#C9A96E] uppercase tracking-wider">
                {t("dashboard.weiterhören")}
              </p>
            </div>
            <h3 className="text-sm font-semibold text-[#2d3a1e] truncate">
              {chapterTitle}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <div className="flex-1 h-1.5 bg-[#d4dcc4]/50 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#C9A96E] rounded-full transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="text-[10px] text-[#8a9680] whitespace-nowrap">
                {formatTime(latest.currentTimeSec)} /{" "}
                {formatTime(latest.durationSec)}
              </span>
            </div>
          </div>

          {/* Play Button */}
          <div className="w-10 h-10 rounded-full bg-[#C9A96E] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-md">
            <Play className="w-4 h-4 text-white ml-0.5" />
          </div>
        </div>
      </div>
    </Link>
  );
}

/* ─── Hörbuch-Statistiken Widget ─────────────────────────────── */
interface StatsData {
  totalListeningTimeSec: number;
  chaptersCompleted: number;
  totalChapters: number;
  avgProgress: number;
  listeningDays: number;
}

function AudiobookStatsWidget({ stats }: { stats?: StatsData | null }) {
  const { t } = useI18n();
  if (!stats) return null;
  if (stats.totalListeningTimeSec === 0) return null;

  const timeStr =
    Math.floor(stats.totalListeningTimeSec / 3600) > 0
      ? `${Math.floor(stats.totalListeningTimeSec / 3600)}h ${Math.floor((stats.totalListeningTimeSec % 3600) / 60)}m`
      : `${Math.floor(stats.totalListeningTimeSec / 60)}m`;

  return (
    <div className="rounded-2xl border border-[#6b7f4e]/20 bg-gradient-to-r from-[#6b7f4e]/5 to-[#C9A96E]/5 p-4">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-4 h-4 text-[#6b7f4e]" />
        <p className="text-xs font-medium text-[#6b7f4e] uppercase tracking-wider">
          {t("dashboard.hoerbuchStats")}
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="text-center">
          <p className="text-lg font-bold text-[#2d3a1e]">{timeStr}</p>
          <p className="text-[10px] text-[#8a9680]">{t("dashboard.gehoert")}</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-[#2d3a1e]">
            {stats.chaptersCompleted}/{stats.totalChapters}
          </p>
          <p className="text-[10px] text-[#8a9680]">{t("dashboard.fertig")}</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-[#2d3a1e]">
            {stats.avgProgress}%
          </p>
          <p className="text-[10px] text-[#8a9680]">{t("dashboard.fortschrittAvg")}</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-[#2d3a1e]">
            {stats.listeningDays}
          </p>
          <p className="text-[10px] text-[#8a9680]">{t("dashboard.hoerTage")}</p>
        </div>
      </div>
    </div>
  );
}

export default function AppDashboard() {
  const { t, locale } = useI18n();
  const ayuCoachHref =
    typeof window !== "undefined" &&
    window.sessionStorage.getItem("ayu:last-voice-session-id")
      ? "/ayu-coach?resume=last-voice"
      : "/ayu-coach";
  const [currentTime, setCurrentTime] = useState(new Date());
  const { client, loading: isLoading } = useClientAuth();
  const GreetingIcon = getGreetingIcon();

  // Mood History laden
  const moodHistory = trpc.chat.getMoodHistory.useQuery(
    { clientId: client?.clientId || "", limit: 14 },
    { enabled: !!client?.clientId }
  );

  // Echte Stats aus der DB laden
  const tagebuchSessions = trpc.chat.getSessions.useQuery(
    { clientId: client?.clientId || "", chatType: "tagebuch" },
    { enabled: !!client?.clientId }
  );
  const coachSessions = trpc.chat.getSessions.useQuery(
    { clientId: client?.clientId || "", chatType: "ayu" },
    { enabled: !!client?.clientId }
  );

  // Dashboard Stats (für Tage aktiv etc.)
  const { data: stats } = trpc.clientAuth.dashboardStats.useQuery(undefined, {
    staleTime: 60_000,
    enabled: !!client,
  });

  // Hörbuch-Fortschritt für "Weiterhören" Widget
  const { data: audiobookProgressData } =
    trpc.stripe.getAudiobookProgress.useQuery(
      { clientId: client?.clientId || "" },
      { enabled: !!client?.clientId, staleTime: 30_000 }
    );

  // Hörbuch-Statistiken
  const { data: audiobookStatsData } = trpc.stripe.getStats.useQuery(
    { clientId: client?.clientId || "" },
    { enabled: !!client?.clientId, staleTime: 60_000 }
  );

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const todayFormatted = currentTime.toLocaleDateString(locale === "en" ? "en-GB" : "de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  // Daily Quote
  const dailyQuote = useMemo(() => getDailyQuote(), []);

  if (isLoading || !client) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-5 h-5 animate-spin text-[#6b7f4e]" />
      </div>
    );
  }

  const firstName = client.firstName || "dort";

  // Echte Zahlen
  const tagebuchCount = tagebuchSessions.data?.length ?? 0;
  const coachCount = coachSessions.data?.length ?? 0;

  // Coaching-Stil anzeigen
  const coachingStyleLabels: Record<string, string> = {
    empathisch: t("dashboard.coachingEmpathisch"),
    direkt: t("dashboard.coachingDirekt"),
    freundschaftlich: t("dashboard.coachingFreundschaftlich"),
  };
  const coachingStyleDisplay = client.coachingStyle
    ? coachingStyleLabels[client.coachingStyle] || client.coachingStyle
    : null;

  // Dosha/Phänotyp
  const doshaProfile = client.doshaProfile as {
    primary?: string;
    secondary?: string;
    scores?: Record<string, number>;
  } | null;

  const greeting = client.onboardingComplete
    ? t("dashboard.greetingSubtitle")
    : t("dashboard.greetingSubtitleNew");

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      {/* ── Greeting Section ── */}
      <div data-tour="greeting">
        <div className="flex items-center gap-2 text-[#8a9680] text-sm mb-2">
          <GreetingIcon className="w-4 h-4" />
          <span>{todayFormatted}</span>
        </div>
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#C9A96E]/20 shadow-sm shrink-0">
            {client.avatarUrl ? (
              <img
                src={client.avatarUrl}
                alt={`${client.firstName} ${client.lastName}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#6b7f4e]/20 to-[#C9A96E]/20 flex items-center justify-center">
                <span className="text-lg font-semibold text-[#6b7f4e]">
                  {(client.firstName[0] || "").toUpperCase()}
                  {(client.lastName[0] || "").toUpperCase()}
                </span>
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h1
              className="text-3xl md:text-4xl font-semibold text-[#2d3a1e]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {getGreetingByTime(t as (key: string) => string)}, {firstName}.
            </h1>
            <p className="text-[#5a6b4a] mt-1 text-[15px] leading-relaxed max-w-lg">
              {greeting}
            </p>
          </div>
        </div>
      </div>

      {/* ── Telegram-Verknüpfung (prominent für neue Nutzer) ── */}
      {/* ── PWA Install Banner (einmalig) ── */}
      <PwaInstallBanner />

      <div data-tour="telegram-card">
        <TelegramDashboardCard clientId={client.clientId} />
      </div>

      {/* ── Check-In Banner (wenn fällig) ── */}
      <CheckInBanner />

      {/* ── Hörbuch-Gruppe ── */}
      <div className="flex flex-col gap-4">
        <ContinueListeningWidget progressData={audiobookProgressData} />
        <AudiobookStatsWidget stats={audiobookStatsData} />
      </div>

      {/* ── Paket-Badge + Phänotyp Card ── */}
      <div className="rounded-2xl bg-gradient-to-br from-[#6b7f4e]/10 to-[#6b7c3e]/10 border border-[#d4dcc4] p-5 space-y-4">
        {/* Paket-Anzeige */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`p-2.5 rounded-xl ${client.packageType === "premium" ? "bg-[#8b6914]/15 text-[#8b6914]" : "bg-[#6b7f4e]/15 text-[#6b7f4e]"}`}
            >
              {client.packageType === "premium" ? (
                <Crown className="w-5 h-5" />
              ) : (
                <Sparkle className="w-5 h-5" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-[#2d3a1e]">
                  {client.packageType === "premium"
                    ? t("dashboard.packagePremium")
                    : t("dashboard.packageBasis")}
                </h3>
                <span
                  className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    client.packageType === "premium"
                      ? "bg-[#8b6914]/15 text-[#8b6914]"
                      : "bg-[#6b7f4e]/15 text-[#6b7f4e]"
                  }`}
                >
                  {client.packageType === "premium" ? t("common.premium") : t("common.basis")}
                </span>
              </div>
              <p className="text-xs text-[#8a9680]">
                {client.packageType === "premium"
                  ? t("dashboard.packageDescPremium")
                  : t("dashboard.packageDescBasis")}
              </p>
            </div>
          </div>
          {client.packageType === "basis" && (
            <div className="flex flex-col items-end gap-1">
              <UpgradeButton />
              <PromoCodeInput compact />
            </div>
          )}
        </div>

        {/* Phänotyp */}
        {doshaProfile?.primary && (
          <div className="flex items-start gap-4 pt-3 border-t border-[#d4dcc4]">
            <div className="p-3 rounded-xl bg-[#6b7f4e]/20 text-[#6b7f4e]">
              <Heart className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-[#5a6b4a] uppercase tracking-wider mb-1">
                {t("dashboard.phenotyp")}
              </p>
              <h2 className="text-lg font-bold text-[#2d3a1e]">
                {PHENOTYPES[doshaProfile.primary as PhenotypeId]?.name ||
                  doshaProfile.primary}
                {doshaProfile.secondary && (
                  <span className="text-[#6b7f4e]">
                    {" "}
                    /{" "}
                    {PHENOTYPES[doshaProfile.secondary as PhenotypeId]?.name ||
                      doshaProfile.secondary}
                  </span>
                )}
              </h2>
              <p className="text-sm text-[#5a6b4a] mt-1">
                {t("dashboard.basedOnQuestionnaire")}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── Quick Actions (MENTO-Style: 2 große Karten + 1 Kompass) ── */}
      <div className="flex flex-col gap-6">
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        data-tour="quick-actions"
      >
        {/* Tagebuch Card */}
        <Link href="/tagebuch">
          <div className="group relative overflow-hidden rounded-2xl bg-white border border-[#6b7f4e]/20 p-6 cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-[#6b7c3e]/10 hover:border-[#6b7c3e]/40">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#6b7c3e]/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500" />
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-[#6b7c3e]/15 flex items-center justify-center mb-4">
                <BookOpen className="w-5 h-5 text-[#6b7c3e]" />
              </div>
              <h3
                className="text-xl font-semibold text-[#2d3a1e] mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("nav.tagebuch")}
              </h3>
              <p className="text-sm text-[#5a6b4a] leading-relaxed">
                {t("dashboard.tagebuchSubtitle")}
              </p>
              <div className="flex items-center gap-1.5 mt-4 text-[#6b7c3e] text-sm font-medium group-hover:gap-2.5 transition-all">
                <span>{t("dashboard.tagebuchWriteEntry")}</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </Link>

        {/* AYU Coach Card */}
        <Link href={ayuCoachHref}>
          <div className="group relative overflow-hidden rounded-2xl bg-white border border-[#d4dcc4] p-6 cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-[#6b7f4e]/10 hover:border-[#6b7f4e]/40">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#6b7f4e]/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500" />
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-[#6b7f4e]/15 flex items-center justify-center mb-4">
                <MessageCircle className="w-5 h-5 text-[#6b7f4e]" />
              </div>
              <h3
                className="text-xl font-semibold text-[#2d3a1e] mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("dashboard.annamSessions")}
              </h3>
              <p className="text-sm text-[#5a6b4a] leading-relaxed">
                {t("dashboard.coachSubtitle")}
              </p>
              <div className="flex items-center gap-1.5 mt-4 text-[#6b7f4e] text-sm font-medium group-hover:gap-2.5 transition-all">
                <span>{t("dashboard.startSession")}</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Kompass Card (volle Breite, kompakter) */}
      <Link href="/kompass">
        <div className="group relative overflow-hidden rounded-2xl bg-white border border-[#6b7f4e]/20 p-5 cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-[#8b6914]/10 hover:border-[#8b6914]/40">
          <div className="absolute top-0 right-0 w-24 h-24 bg-[#8b6914]/5 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500" />
          <div className="relative flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-[#8b6914]/15 flex items-center justify-center">
              <Compass className="w-5 h-5 text-[#8b6914]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-[#2d3a1e]">{t("nav.kompass")}</h3>
              <p className="text-sm text-[#5a6b4a]">
                {t("dashboard.kompassSubtitle")}
              </p>
            </div>
            <ArrowRight className="w-4 h-4 text-[#8b6914] group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </Link>
      </div>

      {/* ── Stats Row ── */}
      <div
        className="grid grid-cols-2 md:grid-cols-4 gap-3"
        data-tour="stats-row"
      >
        <Link href="/tagebuch">
          <div className="bg-white rounded-xl p-4 border border-[#d4dcc4] cursor-pointer hover:bg-[#f0f3eb] hover:shadow-sm transition-all duration-200 group">
            <div className="flex items-center gap-2 text-[#6b7c3e]/70 text-xs mb-2">
              <BookOpen className="w-3.5 h-3.5" />
              <span>{t("nav.tagebuch")}</span>
              <ArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-60 transition-opacity" />
            </div>
            <p
              className="text-2xl font-semibold text-[#2d3a1e]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {tagebuchSessions.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#6b7c3e]" />
              ) : (
                tagebuchCount
              )}
            </p>
            <p className="text-xs text-[#8a9680] mt-0.5">
              {tagebuchCount === 1 ? t("dashboard.tagebuchEntry") : t("dashboard.tagebuchEntries")}
            </p>
          </div>
        </Link>

        <Link href={ayuCoachHref}>
          <div className="bg-white rounded-xl p-4 border border-[#d4dcc4] cursor-pointer hover:bg-[#f0f3eb] hover:shadow-sm transition-all duration-200 group">
            <div className="flex items-center gap-2 text-[#6b7f4e]/70 text-xs mb-2">
              <MessageCircle className="w-3.5 h-3.5" />
              <span>{t("dashboard.coachSitzungen")}</span>
              <ArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-60 transition-opacity" />
            </div>
            <p
              className="text-2xl font-semibold text-[#2d3a1e]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {coachSessions.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin text-[#6b7f4e]" />
              ) : (
                coachCount
              )}
            </p>
            <p className="text-xs text-[#8a9680] mt-0.5">
              {coachCount === 1 ? t("dashboard.coachSession") : t("dashboard.coachSessions")}
            </p>
          </div>
        </Link>

        <div className="bg-white rounded-xl p-4 border border-[#d4dcc4]">
          <div className="flex items-center gap-2 text-[#8b6914]/70 text-xs mb-2">
            <Calendar className="w-3.5 h-3.5" />
            <span>{t("dashboard.aktivitaet")}</span>
          </div>
          <p
            className="text-2xl font-semibold text-[#2d3a1e]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {stats?.daysActive ?? 1}
          </p>
          <p className="text-xs text-[#8a9680] mt-0.5">
            {(stats?.daysActive ?? 1) === 1 ? t("dashboard.aktivTag") : t("dashboard.aktiv")}
          </p>
        </div>

        <Link href="/profil">
          <div className="bg-white rounded-xl p-4 border border-[#d4dcc4] cursor-pointer hover:bg-[#f0f3eb] hover:shadow-sm transition-all duration-200 group">
            <div className="flex items-center gap-2 text-[#E8856C]/70 text-xs mb-2">
              <Flame className="w-3.5 h-3.5" />
              <span>{t("dashboard.coachingStil")}</span>
              <ArrowRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-60 transition-opacity" />
            </div>
            <p
              className="text-lg font-semibold text-[#2d3a1e]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {coachingStyleDisplay || "—"}
            </p>
            <p className="text-xs text-[#8a9680] mt-0.5">
              {coachingStyleDisplay ? t("dashboard.deinStil") : t("dashboard.stilAendern")}
            </p>
          </div>
        </Link>
      </div>

      {/* ── Gamification Widget ── */}
      <div data-tour="gamification">
        <GamificationWidget clientId={client.clientId} />
      </div>

      {/* ── Daily Quote ── */}
      <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#6b7f4e]" />
          <h3 className="text-sm font-medium text-[#6b7f4e]">{t("dashboard.tippDesTages")}</h3>
        </div>
        <blockquote className="text-[#4a5e38] text-sm leading-relaxed italic">
          "{dailyQuote.text}"
        </blockquote>
        <p className="text-xs text-[#8a9680] mt-2">— {dailyQuote.author}</p>
      </div>

      {/* ── Stimmungsverlauf ── */}
      {(() => {
        const moodEntries = (moodHistory.data || [])
          .filter((e: { mood: string | null }) => e.mood)
          .reverse();

        if (moodEntries.length === 0) return null;

        // Tendenz berechnen
        const getTrend = () => {
          if (moodEntries.length < 2)
            return {
              label: t("dashboard.stimmungToWenig"),
              icon: "—",
              color: "text-[#8a9680]",
            };
          const values = moodEntries.map(
            (e: { mood: string | null }) =>
              (MOOD_CONFIG[e.mood || ""] || MOOD_CONFIG.neutral).value
          );
          const recentCount = Math.min(3, Math.floor(values.length / 2)) || 1;
          const recent = values.slice(-recentCount);
          const earlier = values.slice(0, recentCount);
          const avgRecent =
            recent.reduce((a: number, b: number) => a + b, 0) / recent.length;
          const avgEarlier =
            earlier.reduce((a: number, b: number) => a + b, 0) / earlier.length;
          const diff = avgRecent - avgEarlier;
          if (diff > 0.3)
            return { label: t("dashboard.stimmungAufwaerts"), icon: "↗", color: "text-[#6b7c3e]" };
          if (diff < -0.3)
            return { label: t("dashboard.stimmungAbwaerts"), icon: "↘", color: "text-[#E8856C]" };
          return { label: t("dashboard.stimmungStabil"), icon: "→", color: "text-[#8FA89A]" };
        };
        const trend = getTrend();

        // Durchschnitt berechnen
        const values = moodEntries.map(
          (e: { mood: string | null }) =>
            (MOOD_CONFIG[e.mood || ""] || MOOD_CONFIG.neutral).value
        );
        const avgMood =
          values.length > 0
            ? (
                values.reduce((a: number, b: number) => a + b, 0) /
                values.length
              ).toFixed(1)
            : "0";
        const avgLabel =
          Number(avgMood) >= 4
            ? t("tagebuch.gut")
            : Number(avgMood) >= 3
              ? t("tagebuch.okay")
              : t("dashboard.stimmungAusbaufaehig");

        // SVG Line Chart Daten
        const chartWidth = 280;
        const chartHeight = 80;
        const padding = 10;
        const points = moodEntries.map((e: any, i: number) => {
          const x =
            padding +
            (i / Math.max(moodEntries.length - 1, 1)) *
              (chartWidth - 2 * padding);
          const val = (MOOD_CONFIG[e.mood || ""] || MOOD_CONFIG.neutral).value;
          const y =
            chartHeight -
            padding -
            ((val - 1) / 4) * (chartHeight - 2 * padding);
          return { x, y, val, mood: e.mood || "neutral" };
        });
        const linePath = points
          .map((p: any, i: number) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`)
          .join(" ");
        const areaPath =
          linePath +
          ` L ${points[points.length - 1]?.x || 0} ${chartHeight} L ${points[0]?.x || 0} ${chartHeight} Z`;

        return (
          <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-[#6b7c3e]" />
              <h3 className="text-sm font-medium text-[#5a6b4a]">
                {t("dashboard.stimmungsverlauf")}
              </h3>
              <div
                className={`ml-auto flex items-center gap-1.5 ${trend.color}`}
              >
                <span className="text-sm font-medium">{trend.icon}</span>
                <span className="text-[10px] font-medium">{trend.label}</span>
              </div>
            </div>

            {/* Statistik-Zeile */}
            <div className="flex items-center gap-4 mb-3 text-[10px] text-[#8a9680]">
              <span>
                Ø {avgMood}/5 ({avgLabel})
              </span>
              <span>{moodEntries.length} {t("dashboard.tagebuchEntries")}</span>
              <span className="ml-auto">
                {moodEntries.length > 0 &&
                  new Date(moodEntries[0].createdAt).toLocaleDateString(
                    locale === "en" ? "en-GB" : "de-DE",
                    { day: "numeric", month: "short" }
                  )}
                {moodEntries.length > 1 &&
                  ` – ${new Date(moodEntries[moodEntries.length - 1].createdAt).toLocaleDateString(locale === "en" ? "en-GB" : "de-DE", { day: "numeric", month: "short" })}`}
              </span>
            </div>

            {/* SVG Line Chart */}
            {moodEntries.length >= 2 && (
              <div className="mb-3">
                <svg
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  className="w-full h-20"
                >
                  <defs>
                    <linearGradient
                      id="moodGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#6b7f4e" stopOpacity="0.2" />
                      <stop
                        offset="100%"
                        stopColor="#6b7f4e"
                        stopOpacity="0.02"
                      />
                    </linearGradient>
                  </defs>
                  {/* Grid lines */}
                  {[1, 2, 3, 4, 5].map(v => {
                    const y =
                      chartHeight -
                      padding -
                      ((v - 1) / 4) * (chartHeight - 2 * padding);
                    return (
                      <line
                        key={v}
                        x1={padding}
                        y1={y}
                        x2={chartWidth - padding}
                        y2={y}
                        stroke="#6b7f4e"
                        strokeOpacity="0.12"
                        strokeWidth="0.5"
                      />
                    );
                  })}
                  {/* Area fill */}
                  <path d={areaPath} fill="url(#moodGradient)" />
                  {/* Line */}
                  <path
                    d={linePath}
                    fill="none"
                    stroke="#6b7c3e"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  {/* Data points */}
                  {points.map((p: any, i: number) => (
                    <circle
                      key={i}
                      cx={p.x}
                      cy={p.y}
                      r={i === points.length - 1 ? 4 : 2.5}
                      fill={(MOOD_CONFIG[p.mood] || MOOD_CONFIG.neutral).color}
                      stroke="#ffffff"
                      strokeWidth="1.5"
                    />
                  ))}
                </svg>
              </div>
            )}

            {/* Emoji Timeline */}
            <div className="flex items-center justify-between gap-1">
              {moodEntries.slice(-14).map((entry: any, idx: number) => {
                const config =
                  MOOD_CONFIG[entry.mood || ""] || MOOD_CONFIG.neutral;
                const dateStr = new Date(entry.createdAt).toLocaleDateString(
                  locale === "en" ? "en-GB" : "de-DE",
                  { day: "numeric", month: "short" }
                );
                const isLast = idx === Math.min(moodEntries.length, 14) - 1;
                return (
                  <div
                    key={entry.id || idx}
                    className="flex flex-col items-center gap-1.5 flex-1 group relative"
                  >
                    {idx < Math.min(moodEntries.length, 14) - 1 && (
                      <div className="absolute top-4 left-[55%] right-[-45%] h-px bg-[#6b7f4e]/10 z-0" />
                    )}
                    <div
                      className={`relative z-10 text-lg md:text-2xl transition-transform duration-200 group-hover:scale-125 ${isLast ? "drop-shadow-sm" : ""}`}
                    >
                      {config.emoji}
                    </div>
                    <span
                      className={`text-[9px] md:text-[10px] leading-tight ${isLast ? "text-[#5a6b4a] font-medium" : "text-[#8a9680]/50"}`}
                    >
                      {dateStr}
                    </span>
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-[#2d3a1e] text-[#2d3a1e] text-[9px] px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
                      {config.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ─── PWA Install Banner (one-time, dismissible) ─── */
function PwaInstallBanner() {
  const { t } = useI18n();
  const {
    canInstall,
    isInstalled,
    isIOS,
    installPwa,
    hasNativePrompt,
    wasDismissed,
    dismissInstall,
  } = usePwaInstall();
  const [dismissed, setDismissed] = useState(false);

  // Don't show if already installed, dismissed, or can't install
  if (isInstalled || dismissed || wasDismissed() || !canInstall) return null;

  const handleDismiss = () => {
    dismissInstall();
    setDismissed(true);
  };

  const handleInstall = async () => {
    if (hasNativePrompt) {
      const success = await installPwa();
      if (success) {
        toast.success(t("dashboard.pwaInstalled"));
        setDismissed(true);
      }
    }
  };

  return (
    <div className="rounded-2xl bg-gradient-to-r from-[#6b7f4e]/10 to-[#c8a97e]/10 border border-[#d4dcc4] p-4 relative animate-in slide-in-from-top duration-300">
      {/* Dismiss X */}
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-[#8a9680] hover:text-[#2d3a1e] transition-colors z-10"
        aria-label="Schließen"
      >
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {hasNativePrompt ? (
        /* Android: Simple one-tap install */
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6b7f4e] to-[#5a6b3e] flex items-center justify-center flex-shrink-0 shadow-md">
            <svg
              className="w-6 h-6 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[#2d3a1e]">
              {t("dashboard.pwaInstallTitle")}
            </h3>
            <p className="text-xs text-[#8a9680] mt-0.5">
              {t("dashboard.pwaInstallSubtitle")}
            </p>
          </div>
          <button
            onClick={handleInstall}
            className="px-4 py-2 rounded-xl bg-[#6b7f4e] text-white text-xs font-medium hover:bg-[#5a6e3f] transition-colors flex-shrink-0 shadow-sm"
          >
            {t("common.install")}
          </button>
        </div>
      ) : isIOS ? (
        /* iOS: Step-by-step visual guide */
        <div className="pr-6">
          <h3 className="text-sm font-semibold text-[#2d3a1e] mb-3">
            {t("dashboard.pwaInstallTitle")}
          </h3>
          <div className="space-y-2.5">
            {/* Step 1 */}
            <div className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-[#6b7f4e] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                1
              </span>
              <p className="text-xs text-[#4a5a3a] leading-relaxed">
                {t("dashboard.iosStep1")}
                <span className="inline-flex items-center justify-center w-5 h-5 bg-[#007AFF] rounded ml-1 align-middle">
                  <svg
                    className="w-3 h-3 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </span>
              </p>
            </div>
            {/* Step 2 */}
            <div className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-[#6b7f4e] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                2
              </span>
              <p className="text-xs text-[#4a5a3a] leading-relaxed">
                {t("dashboard.iosStep2")}
                <span className="inline-flex items-center justify-center w-5 h-5 bg-gray-100 rounded ml-1 align-middle">
                  <svg
                    className="w-3 h-3 text-gray-600"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <line x1="12" y1="8" x2="12" y2="16" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                  </svg>
                </span>
              </p>
            </div>
            {/* Step 3 */}
            <div className="flex items-start gap-3">
              <span className="w-5 h-5 rounded-full bg-[#6b7f4e] text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                3
              </span>
              <p className="text-xs text-[#4a5a3a] leading-relaxed">
                {t("dashboard.iosStep3")}
              </p>
            </div>
          </div>
          <p className="text-[10px] text-[#8a9680] mt-3">
            {t("dashboard.iosFinalNote")}
          </p>
        </div>
      ) : (
        /* Fallback for other browsers */
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6b7f4e] to-[#5a6b3e] flex items-center justify-center flex-shrink-0 shadow-md">
            <svg
              className="w-6 h-6 text-white"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[#2d3a1e]">
              {t("dashboard.pwaInstallTitle")}
            </h3>
            <p className="text-xs text-[#8a9680] mt-0.5">
              {t("dashboard.pwaInstallFallback")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
